/* global YT:false */
import slideScroller from '../classes/slideScroller';
import InViewStyle from '../classes/inViewStyle';
import HoverShare from '../components/hoverShare';
import { loadScript } from '../utility';

/**
 * This class controls a Video Player and Playlist component.
 * The Video Player is a regular <div>, which the YT API updates to contain an <iframe>
 * The YT API is the interface for playing videos.
 *
 * The Playlist component is a visual "filmstrip" of video thumbnails.
 * Clicking on a thumbnail will tell the YT API to play that video.
 *
 * When a video completes playing to the end,
 * The YT API is used to play the next video in the playlist.
 */
export default class YouTubeVideoPlayer {
  constructor(containerEl) {
    this.containerEl = containerEl;
    this.videoIframe = this.containerEl.querySelector('[data-video-player-iframe]');
    this.hoverShareEl = this.containerEl.querySelector('[data-hover-share]');
    // Holds a reference to the YouTube player instance
    this.youtubePlayer;

    // Maps each element in the video thumbnail strip
    // to its child that should receive the active style.
    // This avoids calls to querySelector() later on.
    this.clickTargetToClassTargetMap = new Map();

    // Set the first video as the active video.
    // This assumes the correct classes and iframe src are rendered server-side.
    this.state = {
      videoIndex: 0,
    };

    this.initSubComponents();
    this.mapThumbsToPlayer();
    this.events();

    this.initState();
    this.initYouTubeApi();
  }

  initSubComponents() {
    new InViewStyle(this.containerEl.querySelector('[data-in-view-container="homepageVideoSlider"]'));
    new slideScroller(this.containerEl.querySelector('[data-video-strip]'));
    if (this.hoverShareEl) {
      this.hoverShare = new HoverShare(this.hoverShareEl);
    }
  }

  mapThumbsToPlayer() {
    this.containerEl.querySelectorAll('[data-video-strip-item]').forEach(video =>
      this.clickTargetToClassTargetMap.set(video, video.querySelector('.video-card')));
  }

  events() {
    [...this.clickTargetToClassTargetMap.keys()].forEach((videoItem, i) => {
      videoItem.addEventListener('click', () => {
        this.updateState(i);
        this.updateDOM(true);
      });
    });
  }

  initState() {
    this.updateState(0);
  }

  updateState(newIndex) {
    this.state = {
      videoIndex: newIndex,
    };
  }

  updateDOM(playNow) {
    // Before updating the YT player,
    // ensure the YT global API object is available
    if (this.youtubePlayer.loadVideoById) {
      this.updatePlayer(playNow === true);
    }
    this.updatePlaylist();
    if (this.hoverShareEl) {
      this.updateShareLinks(this._getActiveVideo().dataset);
    }
  }

  updateShareLinks({ facebook, twitter, external }) {
    this.hoverShare.setAllUrls(facebook, twitter, external);
  }

  updatePlayer(playNow) {
    if (playNow) {
      this.youtubePlayer.loadVideoById(this._getVideoIdByIndex(this.state.videoIndex));
    } else {
      this.youtubePlayer.cueVideoById(this._getVideoIdByIndex(this.state.videoIndex));
    }
  }

  updatePlaylist() {
    // Remove the active class from the previous active element
    this.containerEl.querySelectorAll('.video-card--active').forEach(thumbnail => thumbnail.classList.remove('video-card--active'));
    // Add the active class to the current active element
    this._getActiveVideo().classList.add('video-card--active');
  }

  initYouTubeApi() {
    // Add YouTube API functions to global space
    window.onYouTubeIframeAPIReady = this.onYouTubeIframeAPIReady.bind(this);
    return loadScript('https://www.youtube.com/iframe_api');
  }

  onYouTubeIframeAPIReady() {
    // Creates the player object
    this.youtubePlayer = new YT.Player(this.videoIframe, {
      videoId: this._getVideoIdByIndex(this.state.videoIndex),
      host: 'https://www.youtube-nocookie.com',
      events: {
        'onReady': this.updateDOM.bind(this),
        'onStateChange': this.onYouTubePlayerStateChange.bind(this),
      },
    });
  }

  // Advance to next slide once video ends if it's not the last video
  onYouTubePlayerStateChange(event) {
    if (event.data === YT.PlayerState.ENDED) {
      this.updateState(this.state.videoIndex + 1);
      this.updateDOM(true);
    }
  }

  _getVideoIdFromEl(el) {
    return el.dataset.youtube;
  }

  _getVideoByIndex(i) {
    return [...this.clickTargetToClassTargetMap.keys()][i];
  }

  _getActiveVideo() {
    return this._getVideoByIndex(this.state.videoIndex);
  }

  _getVideoIdByIndex(i) {
    return this._getVideoIdFromEl(this._getVideoByIndex(i));
  }
}

